'use client';

import { domAnimation, LazyMotion, m, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';

import { DURATION_POSITION_SMALL_MS, EASE_DEFAULT_MOTION } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';

// ---------------------------------------------------------------------------
// StepIndicator — segmented progress bar for multi-step modals.
//
// Mirrors the pattern duplicated inline across SendGiftModal,
// CreateProposalDialog, etc. Each segment animates from background-tertiary
// to foreground-primary over 150 ms (`scaleX` of an inner motion.div) once
// its step completes. Reduced-motion users see an instant swap.
//
// Caller renders the "Step N of M" caption above the bar.
// ---------------------------------------------------------------------------

export interface StepIndicatorProps {
  /** 1-indexed current step. */
  currentStep: number;
  /** Total number of steps to render as segments. */
  totalSteps: number;
  /** When true, the caption "Step N of M" is rendered above the bars.
   *  Defaults to true. Uses the `stepIndicator.stepOf` key for caption text. */
  showCaption?: boolean;
  className?: string;
}

export function StepIndicator({
  currentStep,
  totalSteps,
  showCaption = true,
  className,
}: StepIndicatorProps): React.ReactNode {
  const tShared = useTranslations('stepIndicator');
  const prefersReducedMotion = useReducedMotion();
  const duration = prefersReducedMotion === true ? 0 : DURATION_POSITION_SMALL_MS / 1000;

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {showCaption ? (
        <p className="text-caption text-foreground-secondary">
          {tShared('stepOf', { current: currentStep, total: totalSteps })}
        </p>
      ) : null}
      <LazyMotion features={domAnimation}>
        <div className="flex gap-1">
          {Array.from({ length: totalSteps }, (_, i) => {
            const filled = i < currentStep;
            return (
              <div
                key={`step-${String(i)}`}
                className="relative h-1.5 flex-1 overflow-hidden rounded-full bg-background-tertiary"
              >
                <m.div
                  className="h-full w-full origin-left rounded-full bg-foreground-primary"
                  initial={{ scaleX: filled ? 1 : 0 }}
                  animate={{ scaleX: filled ? 1 : 0 }}
                  transition={{ duration, ease: EASE_DEFAULT_MOTION }}
                />
              </div>
            );
          })}
        </div>
      </LazyMotion>
    </div>
  );
}
